import React, { useEffect, useState } from 'react';
import { Col, Row, Space, Empty } from 'antd';
import { BarChartOutlined, FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { useResponsive } from '../../hooks/useResponsive';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { notificationController } from '../../controllers/notificationController';
import { GradingTable } from '../../components/tables/Grading/GradingTable/GradingTable';
import { IAssessmentsState } from '../../store/reducers/assessments/types';
import { IClassesState } from '../../store/reducers/classes/types';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';

// New layout components
import { 
  PageContainer, 
  ContentCard, 
  StatsCard 
} from '../../components/layouts/PageLayouts';

// Business buttons
import { CreateButton } from '../../components/common/buttons';

// Styled Components for Assessment Cards
const AssessmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const AssessmentCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: rgba(34, 197, 94, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #22c55e 0%, #16a34a 50%, #15803d 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: 1024px) {
    &:hover {
      transform: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
    
    &::before {
      opacity: 1;
    }
    
    &:active {
      transform: scale(0.98);
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
`;

const CardTitle = styled.div`
  flex: 1;
  
  .icon {
    font-size: 18px;
    margin-bottom: 8px;
    display: block;
  }
  
  .name {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 4px 0;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .date {
    font-size: 12px;
    color: #6B7280;
    font-weight: 500;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #16A34A;
  white-space: nowrap;
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22C55E;
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.3); }
  }
`;

const CardInfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
`;

const CardInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  
  .row-icon {
    font-size: 14px;
    width: 20px;
    text-align: center;
    flex-shrink: 0;
  }
  
  .row-label {
    color: #6B7280;
    min-width: 60px;
    flex-shrink: 0;
  }
  
  .row-value {
    color: #1a1a1a;
    font-weight: 500;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SelectButton = styled(BusinessButton)`
  width: 100%;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(0, 0, 0, 0.45);
  
  .icon {
    font-size: 48px;
    margin-bottom: 12px;
    display: block;
  }
  
  .title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 6px;
    color: rgba(0, 0, 0, 0.65);
  }
  
  .description {
    font-size: 13px;
  }
`;

const GradingPage: React.FC = (props: any) => {
    const { isDesktop } = useResponsive();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getGradingsRequest, getAssessmentsRequest, getClassesRequest } = useActions();
    const gradings = useTypedSelector<any>(state => state.gradings);
    const { assessments, loading: assessmentsLoading } = useTypedSelector<IAssessmentsState>(state => state.assessments);
    const { classes } = useTypedSelector<IClassesState>(state => state.classes);

    useEffect(() => {
        // Load gradings and assessments (userId already in localStorage from Keycloak)
        const storedUserId = localStorage.getItem("UserId");
        if (storedUserId && storedUserId !== 'null') {
          // Add userId to URL if not present
          if (!searchParams.get('userId')) {
            setSearchParams({ userId: storedUserId });
          }
          
          getGradingsRequest();
          getAssessmentsRequest();
          getClassesRequest();
        }
      }, [])
  
    // Filter only active assessments
    const activeAssessments = (assessments || []).filter((assessment: any) => assessment.status === 'active');
    
    const handleSelectAssessment = (assessment: any) => {
      const assessmentId = assessment.id;
      const assessmentName = assessment.name || assessment.examName || 'Assessment';
      navigate(`/grading-assessment?assessmentId=${assessmentId}&assessmentName=${encodeURIComponent(assessmentName)}`);
    };

    const formatDate = (dateString: string) => {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    };

    const getClassName = (classId: number) => {
      const classesArray = classes as any[];
      const cls = classesArray?.find((c) => c.id === classId);
      return cls?.name || `Class ${classId}`;
    };
  
    // Calculate grading stats
    const totalGradings = gradings.gradings?.length || 0;
    const completedGradings = gradings.gradings?.filter((g: any) => g.status === 'completed')?.length || 0;
    const pendingGradings = gradings.gradings?.filter((g: any) => g.status === 'pending')?.length || 0;
    const averageScore = totalGradings > 0 
      ? (gradings.gradings?.reduce((sum: number, g: any) => sum + (g.score || 0), 0) / totalGradings).toFixed(1)
      : '0';

    const statsData = [
      {
        title: t('grading:totalGradings'),
        value: totalGradings,
        prefix: <FileTextOutlined />,
        color: '#1677ff',
        trend: 'neutral' as const
      },
      {
        title: t('grading:completed'),
        value: completedGradings,
        prefix: <CheckCircleOutlined />,
        color: '#52c41a',
        trend: 'up' as const,
        trendValue: '+5'
      },
      {
        title: t('grading:pending'),
        value: pendingGradings,
        prefix: <ClockCircleOutlined />,
        color: '#fa8c16',
        trend: 'down' as const,
        trendValue: '-2'
      },
      {
        title: t('grading:averageScore'),
        value: averageScore,
        suffix: '%',
        prefix: <BarChartOutlined />,
        color: '#722ed1',
        trend: 'up' as const,
        trendValue: '+2.1%'
      }
    ];

    const pageActions = (
      <Space>
        <CreateButton businessSize="large">
          {t('grading:addToGrading')}
        </CreateButton>
      </Space>
    );

    const desktopLayout = (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Active Assessments Section */}
        {activeAssessments.length > 0 && (
          <ContentCard
            title="Active Assessments"
            subtitle="Select an assessment to start grading"
          >
            <AssessmentsGrid>
              {activeAssessments.map((assessment: any) => (
                <AssessmentCard key={assessment.id} onClick={() => handleSelectAssessment(assessment)}>
                  <CardHeader>
                    <CardTitle>
                      <span className="icon">📝</span>
                      <h3 className="name">{assessment.examName || assessment.name || 'Untitled Assessment'}</h3>
                      <p className="date">
                        📅 {formatDate(assessment.scheduledDate)}
                      </p>
                    </CardTitle>
                    <StatusBadge>Active</StatusBadge>
                  </CardHeader>
                  
                  <CardInfoList>
                    {/* Type */}
                    <CardInfoRow>
                      <span className="row-icon">📝</span>
                      <span className="row-label">Type</span>
                      <span className="row-value" style={{ textTransform: 'capitalize' }}>
                        {assessment.type || 'quiz'}
                      </span>
                    </CardInfoRow>
                    
                    {/* Classes */}
                    <CardInfoRow>
                      <span className="row-icon">🏫</span>
                      <span className="row-label">Classes</span>
                      <span className="row-value">
                        {assessment.classIds?.length 
                          ? assessment.classIds.map((id: number) => getClassName(id)).join(', ')
                          : assessment.classId 
                            ? getClassName(assessment.classId)
                            : '—'}
                      </span>
                    </CardInfoRow>
                    
                    {/* Duration */}
                    <CardInfoRow>
                      <span className="row-icon">⏱️</span>
                      <span className="row-label">Duration</span>
                      <span className="row-value">
                        {assessment.duration && assessment.duration > 0
                          ? `${assessment.duration} min`
                          : '—'}
                      </span>
                    </CardInfoRow>
                  </CardInfoList>
                  
                  <SelectButton
                    variant="success"
                    businessSize="medium"
                    icon={<CheckCircleOutlined />}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleSelectAssessment(assessment);
                    }}
                  >
                    Select {(assessment.type || 'quiz').charAt(0).toUpperCase() + (assessment.type || 'quiz').slice(1)}
                  </SelectButton>
                </AssessmentCard>
              ))}
            </AssessmentsGrid>
          </ContentCard>
        )}
        
        <StatsCard
          title={t('grading:overview')}
          stats={statsData}
          columns={4}
        />
        
        <ContentCard
          title={t('grading:gradingResults')}
          subtitle={t('grading:viewAndManageGrading')}
        >
          {totalGradings > 0 ? (
            <GradingTable gradings={gradings.gradings} />
          ) : (
            <Empty
              image={<BarChartOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />}
              description={
                <div>
                  <p style={{ fontSize: '16px', margin: '16px 0 8px', color: 'rgba(0, 0, 0, 0.45)' }}>
                    {t('grading:noGradingYet')}
                  </p>
                  <p style={{ fontSize: '14px', margin: 0, color: 'rgba(0, 0, 0, 0.25)' }}>
                    {t('grading:startGradingProcess')}
                  </p>
                </div>
              }
            >
              <CreateButton>
                {t('grading:addToGrading')}
              </CreateButton>
            </Empty>
          )}
        </ContentCard>
      </Space>
    );
  
    const mobileAndTabletLayout = desktopLayout;
    return (
      <>
        <PageTitle title={t('head:aboutUsTitle')} description={t('head:aboutUsDescription')} url={window.location.href} noIndex={true} ></PageTitle>
        <PageContainer
          title={t('grading:grading')}
          subtitle={t('grading:manageGradingProcess')}
          actions={pageActions}
        >
          {desktopLayout}
        </PageContainer>
      </>
    );
  };

export default GradingPage